import { V2_SECTOR_EXPLORATION } from './opportunityCompanionV2';

export const OPPORTUNITY_CATALOG = [
  { id: 'senior_companionship', label: 'Non-medical senior companionship', sector: 'Healthcare', classification: 'NON_IT', signals: ['care', 'Senior assistance'], requirements: ['safety boundaries', 'background-check/provider validation'] },
  { id: 'tutoring', label: 'Tutoring or cultural-language classes', sector: 'Education', classification: 'NON_IT', signals: ['teaching', 'Tutoring or childcare'], requirements: ['subject evidence', 'child-safety boundaries'] },
  { id: 'meal_prep', label: 'Meal-preparation or catering service', sector: 'Small Industries', classification: 'NON_IT', signals: ['food', 'Cooking or catering'], requirements: ['food-safety awareness', 'permitted preparation location'] },
  { id: 'event_coordination', label: 'Cultural and community event coordination', sector: 'Travel / Events', classification: 'NON_IT', signals: ['events', 'Event planning'], requirements: ['coordination evidence', 'local partner validation'] },
  { id: 'property_coordination', label: 'Property and home-service coordination', sector: 'Real Estate', classification: 'TECHNOLOGY_ENABLED_NON_IT', signals: ['property', 'Real-estate support'], requirements: ['licensed-broker boundary', 'vendor/document workflow'] },
  { id: 'bookkeeping_support', label: 'Bookkeeping and finance-document organization', sector: 'Finance', classification: 'NON_IT', signals: ['budgeting', 'Tax or bookkeeping assistance'], requirements: ['bookkeeping evidence', 'privacy and tax boundaries'] },
  { id: 'insurance_admin', label: 'Insurance paperwork and appointment support', sector: 'Insurance', classification: 'NON_IT', signals: ['insurance', 'Insurance education or sales'], requirements: ['privacy boundaries', 'licensed-agent referral'] },
  { id: 'banking_operations', label: 'Banking operations and document support', sector: 'Banking', classification: 'NON_IT', signals: ['banking', 'documentation', 'compliance'], requirements: ['privacy/compliance training', 'qualified referral boundary'] },
  { id: 'logistics_coordination', label: 'Local delivery and logistics coordination', sector: 'Transportation', classification: 'TECHNOLOGY_ENABLED_NON_IT', signals: ['transport', 'Transportation assistance'], requirements: ['vehicle/commercial insurance review', 'local pilot'] },
  { id: 'wellness_events', label: 'Non-clinical wellness and community education', sector: 'Healthcare', classification: 'NON_IT', signals: ['wellness'], requirements: ['no-medical-claims boundary', 'qualified instructor/provider'] },
  { id: 'retail_resale', label: 'Specialty-product resale and local distribution', sector: 'Small Industries', classification: 'NON_IT', signals: ['retail', 'Buying and reselling goods'], requirements: ['supplier validation', 'inventory and margin test'] },
  { id: 'digital_operations', label: 'Digital operations for a local service business', sector: 'IT / Research', classification: 'TECHNOLOGY_ENABLED_NON_IT', signals: ['it_background', 'documentation', 'coordination'], requirements: ['privacy/security basics', 'non-IT customer validation'] },
  { id: 'it_consulting', label: 'IT consulting or software services', sector: 'IT / Research', classification: 'PURE_IT', signals: ['it_background'], requirements: ['portfolio evidence', 'explicit IT preference'] },
  { id: 'legal_admin', label: 'Legal-document organization and referral coordination', sector: 'Legal', classification: 'NON_IT', signals: ['documentation', 'compliance'], requirements: ['non-legal scope', 'qualified attorney referral'] },
  { id: 'research_support', label: 'Research and evidence-analysis support', sector: 'Research', classification: 'IT_ADJACENT', signals: ['documentation', 'quality', 'it_background'], requirements: ['method evidence', 'data/privacy boundaries'] },
];

OPPORTUNITY_CATALOG.forEach((role) => { role.sectors = [role.sector]; });

const DIMENSIONS = ['demonstratedCapability', 'transferableCapability', 'practicalExposure', 'interestFit', 'operatingReadiness', 'lifestyleFit', 'familyModelFit', 'financialFeasibility', 'riskFit', 'learningPotential', 'constraintCompatibility', 'nonITAlignment'];
const WEIGHTS = { demonstratedCapability: 0.18, transferableCapability: 0.14, practicalExposure: 0.10, interestFit: 0.08, operatingReadiness: 0.10, lifestyleFit: 0.08, familyModelFit: 0.05, financialFeasibility: 0.08, riskFit: 0.06, learningPotential: 0.05, constraintCompatibility: 0.05, nonITAlignment: 0.03 };

const clamp = (value) => Math.max(0, Math.min(100, Number(value) || 0));

export const calculateOpportunityFit = (components = {}) => {
  const legacy = {
    demonstratedCapability: components.demonstratedCapability ?? components.capabilityFit,
    transferableCapability: components.transferableCapability ?? components.capabilityFit,
    practicalExposure: components.practicalExposure ?? components.capabilityFit,
    interestFit: components.interestFit,
    operatingReadiness: components.operatingReadiness ?? components.timeFit,
    lifestyleFit: components.lifestyleFit ?? components.workStyleFit,
    familyModelFit: components.familyModelFit ?? components.resourceFit,
    financialFeasibility: components.financialFeasibility ?? components.resourceFit,
    riskFit: components.riskFit ?? components.workStyleFit,
    learningPotential: components.learningPotential ?? components.learningFit,
    constraintCompatibility: components.constraintCompatibility ?? components.workStyleFit,
    nonITAlignment: components.nonITAlignment ?? components.incomeTimelineFit,
  };
  const normalized = Object.fromEntries(DIMENSIONS.map((key) => [key, clamp(legacy[key])]));
  const overallOpportunityFit = Math.round(Object.entries(WEIGHTS).reduce((sum, [key, weight]) => sum + normalized[key] * weight, 0));
  return { ...normalized, capabilityFit: normalized.demonstratedCapability, overallOpportunityFit };
};

export const calculateEvidenceConfidence = ({ completeness = 0, examples = 0, consistency = 0, credentials = 0 } = {}) =>
  Math.round((clamp(completeness) + clamp(examples) + clamp(consistency) + clamp(credentials)) / 4);

const MENTOR_ROLE_OPTIONS = [
  'Career mentor',
  'Project reviewer',
  'Guest speaker',
  'Internship host',
  'Resume and interview advisor',
];

const MENTOR_MISSING_INFORMATION = [
  ['mentor_experience_area', 'Your experience area'],
  ['mentor_example', 'Previous mentoring or leadership examples'],
  ['mentor_audience', 'Preferred audience'],
  ['mentor_format', 'Preferred mentoring format'],
  ['mentor_availability', 'Time availability'],
  ['mentor_compensation', 'Volunteer or paid preference'],
];

const isMeaningfulMentorAnswer = (value) => {
  const normalized = String(value || '').trim().toLowerCase();
  return Boolean(normalized) && !/(still exploring|not sure|need help identifying|not yet)/.test(normalized);
};

export const getMentorPathwayType = (answers = {}) => {
  const support = String(answers.mentor_support_style || '').toLowerCase();
  const goal = String(answers.mentor_primary_goal || '').toLowerCase();
  const preference = String(answers.mentor_compensation || '').toLowerCase();

  if (support.includes('paid coaching') || goal.includes('paid service') || preference.includes('paid')) return 'paid_coaching';
  if (support.includes('short-term projects') || support.includes('host interns')) return 'internship_provider';
  if (goal.includes('talent pipeline')) return 'talent_pipeline';
  if (goal.includes('professional networking')) return 'professional_networking';
  if (goal.includes('supplemental income')) return 'supplemental_income';
  if (goal.includes('community contribution')) return 'community_contribution';
  if (support.includes('still exploring') || goal.includes('not sure')) return 'undecided';
  return 'volunteer_mentoring';
};

export const buildMentorPathwayReport = (answers = {}) => {
  const pathwayType = getMentorPathwayType(answers);
  const missingInformation = MENTOR_MISSING_INFORMATION
    .filter(([id]) => !isMeaningfulMentorAnswer(answers[id]))
    .map(([, label]) => label);
  const assessmentComplete = missingInformation.length === 0;
  const isPaid = pathwayType === 'paid_coaching';
  const isSupplementalIncome = pathwayType === 'supplemental_income';
  const isInternship = pathwayType === 'internship_provider' || pathwayType === 'talent_pipeline';

  const template = !assessmentComplete
    ? {
        title: 'Your UC mentoring pathway is taking shape',
        summary: 'You expressed interest in helping young talent through mentoring, projects, internships, or community talks. We need a little more information before recommending the best role.',
      }
    : isPaid
      ? {
          title: 'Your UC paid coaching or training pathway',
          summary: 'You are exploring a paid coaching or training service. UC can help you define the audience, boundaries, format, and first pilot.',
        }
      : isSupplementalIncome
        ? {
            title: 'Your UC supplemental-income exploration',
            summary: 'You are exploring whether your experience could become a supplemental-income service. UC can help you validate the audience, boundaries, and first pilot before making a recommendation.',
          }
      : isInternship
        ? {
            title: 'Your UC internship-provider pathway',
            summary: 'You are positioned to help emerging talent through supervised projects or internships with clear expectations and safeguards.',
          }
        : {
            title: 'Your UC mentor and volunteer pathway',
            summary: 'You are exploring a contribution to help emerging talent through mentoring, guidance, talks, or community support.',
          };

  return {
    pathwayType,
    assessmentComplete,
    assessmentStatus: assessmentComplete ? 'Ready for UC review' : 'More information needed',
    ...template,
    expressedInterest: answers.mentor_support_style || 'Supporting emerging talent through UC',
    informationNeeded: missingInformation,
    possibleRoles: MENTOR_ROLE_OPTIONS,
    nextQuestions: assessmentComplete
      ? ['What kind of first UC engagement would you like to try?', 'What safeguards or organization channel should UC use?']
      : missingInformation,
    connectionOption: isInternship
      ? 'Request a UC internship-provider conversation'
      : isPaid
        ? 'Request a UC coaching or training discussion'
        : 'Complete my mentor profile',
    showIncomeEstimate: false,
    showNumericalFit: false,
  };
};

const values = (answers = {}) => Object.values(answers).flatMap((value) => Array.isArray(value) ? value : [value]).filter(Boolean);
const has = (answers, text) => values(answers).some((value) => String(value).toLowerCase().includes(String(text).toLowerCase()));
const CAREER_FIELD_TO_CATALOG_SECTOR = {
  'Information technology': 'IT / Research',
  Healthcare: 'Healthcare',
  Education: 'Education',
  'Finance or accounting': 'Finance',
  Insurance: 'Insurance',
  'Legal services': 'Legal',
  'Real estate': 'Real Estate',
  'Community or nonprofit work': 'Education',
};
const selectedSector = (answers) => {
  const interest = String(answers.interest || '');
  const v2Selections = Array.isArray(answers.v2_sector_exploration) ? answers.v2_sector_exploration : [];
  const priorityLabel = answers.v2_sector_priority || v2Selections[0];
  if (priorityLabel === 'Professional-field opportunities' && CAREER_FIELD_TO_CATALOG_SECTOR[answers.career_field]) {
    return CAREER_FIELD_TO_CATALOG_SECTOR[answers.career_field];
  }
  const prioritySector = V2_SECTOR_EXPLORATION[answers.v2_sector_priority]?.catalogSector;
  if (prioritySector) return prioritySector;
  const firstMappedV2Sector = v2Selections
    .map((label) => V2_SECTOR_EXPLORATION[label]?.catalogSector)
    .find(Boolean);
  if (firstMappedV2Sector) return firstMappedV2Sector;
  if (interest.includes('Bank')) return 'Banking';
  if (interest.includes('Legal')) return 'Legal';
  if (interest.includes('Research') && !interest.includes('IT')) return 'Research';
  if (interest.includes('Finance')) return 'Finance';
  if (interest.includes('Insurance')) return 'Insurance';
  if (interest.includes('Healthcare')) return 'Healthcare';
  if (interest.includes('Education')) return 'Education';
  if (interest.includes('Real Estate')) return 'Real Estate';
  if (interest.includes('Travel') || interest.includes('Event')) return 'Travel / Events';
  if (interest.includes('Small') || interest.includes('business')) return 'Small Industries';
  if (interest.includes('IT') || interest.includes('Technology')) return 'IT / Research';
  return '';
};

const isV2 = (answers) => Object.keys(answers || {}).some((key) => key.startsWith('v2_') || ['responsibilities', 'practical_exposure', 'activities_consider', 'identity_constraints', 'family_participant', 'reality_hours', 'reality_setting', 'vision', 'ecosystem_contribution'].includes(key));

const scoreRole = (role, answers, explicitSector) => {
  const evidenceValues = Object.entries(answers).filter(([key]) => !['interest', 'intent', 'intentId', 'career_field', 'v2_non_it_preference', 'v2_12m_goal'].includes(key)).flatMap(([, value]) => Array.isArray(value) ? value : [value]);
  const evidenceItems = evidenceValues.filter((value) => role.signals.some((signal) => String(value).toLowerCase().includes(signal.toLowerCase()))).length;
  const responsibilityCount = Array.isArray(answers.responsibilities) ? answers.responsibilities.length : 0;
  const exposureCount = Array.isArray(answers.practical_exposure) ? answers.practical_exposure.length : 0;
  const directEvidence = clamp(evidenceItems * 22 + (answers.transferable_depth ? 12 : 0));
  const transferable = clamp(responsibilityCount * 13 + (has(answers, 'Performed independently') ? 30 : has(answers, 'Managed others') ? 35 : 10));
  const practical = clamp(exposureCount * 18 + evidenceItems * 8);
  const interested = explicitSector === role.sector || role.signals.some((signal) => has(answers, signal)) ? 78 : 35;
  const hours = answers.reality_hours ? (String(answers.reality_hours).includes('More') ? 95 : String(answers.reality_hours).includes('11') ? 82 : String(answers.reality_hours).includes('6') ? 68 : 45) : 50;
  const itBackground = answers.career_field === 'Information technology';
  const nonITRequested = has(answers, 'prioritize non-IT') || has(answers, 'professional experience as an advantage');
  const pureITRequested = has(answers, 'only want opportunities related');
  const dimensions = {
    demonstratedCapability: directEvidence,
    transferableCapability: transferable,
    practicalExposure: practical,
    interestFit: interested,
    operatingReadiness: clamp(hours + (has(answers, 'customer') ? 8 : 0)),
    lifestyleFit: clamp(50 + (has(answers, 'home') && role.signals.includes('home_based') ? 20 : 0)),
    familyModelFit: has(answers, 'family') || has(answers, 'spouse') ? 80 : 50,
    financialFeasibility: has(answers, 'investment') || has(answers, 'Purchase') ? 55 : 70,
    riskFit: has(answers, 'Fully dependent') ? 45 : 70,
    learningPotential: 65,
    constraintCompatibility: has(answers, 'licensing') && role.requirements.some((item) => item.includes('licensed')) ? 45 : 75,
    nonITAlignment: role.classification === 'NON_IT' ? 90 : role.classification === 'TECHNOLOGY_ENABLED_NON_IT' ? 82 : role.classification === 'IT_ADJACENT' ? 55 : 20,
  };
  let fit = calculateOpportunityFit(dimensions).overallOpportunityFit;
  if (itBackground && nonITRequested && !pureITRequested) {
    fit = Math.round(fit * ({ PURE_IT: 0.75, IT_ADJACENT: 0.90, TECHNOLOGY_ENABLED_NON_IT: 1.05, NON_IT: 1.10 }[role.classification] || 1));
  }
  const completeness = isV2(answers) ? Math.min(100, Object.keys(answers).length * 6) : Object.keys(answers).length ? 60 : 0;
  const confidence = calculateEvidenceConfidence({ completeness, examples: directEvidence, consistency: 50, credentials: has(answers, 'licensed') ? 70 : 10 });
  const confidenceAdjustedFit = Math.round(fit * (0.60 + 0.40 * confidence / 100));
  return { ...role, ...dimensions, capabilityFit: directEvidence, opportunityFit: fit, overallOpportunityFit: confidenceAdjustedFit, evidenceConfidence: confidence, classification: role.classification, recommendationLabel: confidenceAdjustedFit >= 72 && confidence >= 55 ? 'Strong Match' : confidenceAdjustedFit >= 58 && confidence >= 35 ? 'Promising With Preparation' : confidenceAdjustedFit >= 48 ? 'Low-Risk Experiment' : 'General Advisor review' };
};

export const buildOpportunityAssessment = ({ guidanceOnly = false, interestFit = 0, answers = {} } = {}) => {
  if (guidanceOnly) return { recommendations: [], evidenceConfidence: 0 };
  const explicitSector = selectedSector(answers);
  let catalog = OPPORTUNITY_CATALOG;
  if (explicitSector) catalog = catalog.filter((opportunity) => opportunity.sector === explicitSector || (explicitSector === 'Finance / banking' && opportunity.sector === 'Finance'));
  if (!catalog.length) catalog = OPPORTUNITY_CATALOG;
  let recommendations = catalog.map((role) => scoreRole(role, { ...answers, interestFit }, explicitSector));
  const pureITRequested = has(answers, 'only want opportunities related');
  if (!pureITRequested && isV2(answers)) recommendations = recommendations.filter((role) => role.classification !== 'PURE_IT' || role.overallOpportunityFit >= 72);
  recommendations.sort((a, b) => b.overallOpportunityFit - a.overallOpportunityFit || b.evidenceConfidence - a.evidenceConfidence);
  // Preserve relevance once a visitor has explicitly selected a sector. The
  // earlier fallback added unrelated non-IT/digital roles (for example, a
  // finance visitor could also see digital operations) merely to diversify
  // the list. Diversification is useful only while the visitor is undecided.
  if (isV2(answers) && !pureITRequested && !explicitSector) {
    const hasNonIT = recommendations.some((role) => role.classification === 'NON_IT');
    const hasEnabled = recommendations.some((role) => role.classification === 'TECHNOLOGY_ENABLED_NON_IT');
    if (!hasNonIT) recommendations = [scoreRole(OPPORTUNITY_CATALOG.find((role) => role.classification === 'NON_IT'), answers, explicitSector), ...recommendations];
    if (!hasEnabled) recommendations.push(scoreRole(OPPORTUNITY_CATALOG.find((role) => role.classification === 'TECHNOLOGY_ENABLED_NON_IT'), answers, explicitSector));
  }
  const evidenceConfidence = recommendations.length ? Math.min(...recommendations.slice(0, 3).map((item) => item.evidenceConfidence)) : 0;
  return { recommendations: recommendations.slice(0, 5), evidenceConfidence };
};
